// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React from 'react';
import RequireAuth from './RequireAuth';
import { useSharedSession } from './useSharedSession';

/**
 * Authenticated *and* a Qcells platform administrator.
 *
 * NOT the security boundary. `require_platform_admin` on every
 * `/api/v1/provisioning/*` route in the BFF is — this only keeps the UI
 * honest. Until that server-side gate exists, this build must not be deployed
 * anywhere a customer can sign in.
 *
 * What backs the check is still open (an Entra app-role claim vs. an IAM role
 * at global scope). `isPlatformAdmin` below is the single seam that answers
 * changes, so the decision costs one expression, not a refactor.
 */
function PlatformAdminGate({ children }: { children: React.ReactNode }) {
  const { isAdmin, isLoading } = useSharedSession();

  if (isLoading) return null;

  // ponytail: coarse check pending the authz decision — swap for the real
  // claim/role test here and nowhere else.
  const isPlatformAdmin = isAdmin;

  if (!isPlatformAdmin) {
    return <p>You do not have access to Helios Provisioning.</p>;
  }

  return <>{children}</>;
}

export default function RequirePlatformAdmin({ children }: { children: React.ReactNode }) {
  return (
    <RequireAuth>
      <PlatformAdminGate>{children}</PlatformAdminGate>
    </RequireAuth>
  );
}
